import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface CapturedPage {
  id: string;
  sessionId: string;
  timestamp: string;
  url: string;
  reason: string;
  counts: {
    controls: number;
    tables: number;
  };
  index?: number;
  sequence?: string;
  name?: string;
}

export interface IdentifiedStep {
  stepDescription: string;
  elementType: string;
  identifiers: string[];
  isAdded: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ElementCaptureService {
  private readonly baseUrl = `${environment.apiUrl}/element-capture`;

  constructor(private http: HttpClient) {}

  /**
   * Get captured pages for a session
   */
  getCapturedPages(sessionId: string): Observable<CapturedPage[]> {
    return this.http.get<CapturedPage[]>(`${this.baseUrl}/pages/${sessionId}`);
  }

  /**
   * Save captured element data
   */
  saveCapturedElements(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/elements`, data);
  }

  /**
   * Get element map from electron host
   */
  async getElementMap(): Promise<any> {
    return await (window as any).host?.getElementMap();
  }

  /**
   * Get text elements from electron host
   */
  async getTextElements(): Promise<any> {
    return await (window as any).host?.getTextElements();
  }

  /**
   * Enable/disable capture mode
   */
  async setCaptureMode(enabled: boolean, context?: any): Promise<void> {
    await (window as any).host?.captureEnable(enabled, context);
  }

  /**
   * Set split pane position
   */
  setSplitPosition(px: number): void {
    (window as any).host?.setSplit(px);
  }

  /**
   * Navigate to URL
   */
  navigate(url: string): void {
    (window as any).host?.navigate(url);
  }

  /**
   * Browser navigation actions
   */
  back(): void {
    (window as any).host?.back();
  }

  forward(): void {
    (window as any).host?.forward();
  }

  reload(): void {
    (window as any).host?.reload();
  }

  devtools(): void {
    (window as any).host?.devtools();
  }

  extractElements(): void {
    (window as any).host?.extractElements({ save: true });
  }
}
